import { AlertTriangle, Undo2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Note } from '@/shared/ui/states'
import type { BallotEntry } from '../api'
import { TicketChip } from '../vocab'

/** Below this a reading is worth a second look. NOT a threshold anything is rejected on. */
const LOW_CONFIDENCE = 0.75

export interface FieldState {
  label: string
  /** What the MACHINE proposed. Set once, never changed. */
  proposed: string | null
  confidence: number | null
  /** What the human has in the box now. */
  entered: string
  kind: 'candidate' | 'total'
}

/**
 * The extracted numbers, beside the form image, every one editable.
 *
 * ## The machine's value stays visible after a human edits it
 *
 * That is the entire point of this component, and the reason `proposed` is a separate field
 * from `entered` rather than an initial value that gets overwritten. When the two differ the
 * row shows both and says so — and on commit that difference becomes a permanent record, so
 * "the OCR read 412, the agent entered 421" survives long after this screen is closed.
 *
 * ## Low confidence is highlighted, never enforced
 *
 * Confidence is the engine's opinion of its own reading, not a probability that the value is
 * right. A screen that dropped or refused low-confidence readings would be turning that
 * opinion into a decision, which is exactly what a human is here to make.
 *
 * ## The arithmetic is checked live, and blocks nothing
 *
 * Candidate votes that do not sum to the valid vote is the most common real transcription
 * error, so it is worth saying immediately — and it is worth saying rather than refusing,
 * because sometimes the form itself does not add up and that fact has to be recordable.
 */
export function ReviewGrid({
  fields, ballot, imageUrl, onChange,
}: {
  fields: Record<string, FieldState>
  ballot: BallotEntry[]
  imageUrl: string | null
  onChange: (key: string, value: string) => void
}) {
  const entries = Object.entries(fields)
  const candidateKeys = entries.filter(([k]) => k.startsWith('candidacy:'))
  const totalKeys = entries.filter(([k]) => !k.startsWith('candidacy:'))
  const ballotById = new Map(ballot.map((b) => [b.id, b]))

  const num = (v: string) => {
    const n = Number.parseInt(v, 10)
    return Number.isFinite(n) ? n : 0
  }
  const candidateSum = candidateKeys.reduce((t, [, f]) => t + num(f.entered), 0)
  const valid = num(fields.valid_votes?.entered ?? '')
  const registered = num(fields.registered_voters?.entered ?? '')
  const cast = valid + num(fields.rejected_votes?.entered ?? '')
    + num(fields.spoilt_votes?.entered ?? '')

  const problems: string[] = []
  if (valid > 0 && candidateSum !== valid) {
    problems.push(`Candidate votes sum to ${candidateSum.toLocaleString()} but the valid vote `
      + `is ${valid.toLocaleString()}.`)
  }
  if (registered > 0 && cast > registered) {
    problems.push(`${cast.toLocaleString()} ballots cast against ${registered.toLocaleString()} `
      + 'registered voters.')
  }

  return (
    <div className="grid gap-3 lg:grid-cols-2">
      {/* The paper. Everything to the right is a claim about what is in this image. */}
      <div className="order-2 lg:order-1">
        {imageUrl ? (
          <a href={imageUrl} target="_blank" rel="noreferrer"
            className="block rounded-md border border-border overflow-hidden bg-muted">
            <img src={imageUrl} alt="The uploaded results form"
              className="w-full max-h-[28rem] object-contain" />
          </a>
        ) : (
          <div className="rounded-md border border-dashed border-border px-4 py-10 text-center
                          text-erp-xs text-muted-foreground">
            The form image could not be loaded. The numbers below are still the machine's
            reading of it — but check them against the paper before committing.
          </div>
        )}
        <p className="text-erp-xs text-muted-foreground mt-1">
          Open the image in a new tab to zoom. Every number on the right is a claim about
          what is on this page.
        </p>
      </div>

      <div className="order-1 lg:order-2 space-y-3">
        {problems.length > 0 && (
          <Note tone="bad">
            <strong>The arithmetic does not hold.</strong>
            <ul className="mt-1 space-y-0.5 text-erp-xs">
              {problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
            <span className="block mt-1.5 text-erp-xs">
              This does not stop you committing — sometimes the form itself does not add up,
              and that fact has to be recordable. It will appear on the Reconciliation tab.
            </span>
          </Note>
        )}

        <Section title="Candidates">
          {candidateKeys.map(([key, f]) => {
            const b = ballotById.get(key.slice('candidacy:'.length))
            return (
              <FieldRow key={key} fieldKey={key} field={f} onChange={onChange}
                trailing={b && (
                  <TicketChip shortName={b.partyShortName} colourHex={b.partyColourHex}
                    independent={b.independent} />
                )} />
            )
          })}
          {candidateKeys.length === 0 && (
            <p className="text-erp-xs text-muted-foreground px-1 py-2">
              No cleared candidacies for this race — the ballot has to exist before its votes
              can be recorded.
            </p>
          )}
        </Section>

        <Section title="The form's own totals">
          {totalKeys.map(([key, f]) => (
            <FieldRow key={key} fieldKey={key} field={f} onChange={onChange} />
          ))}
        </Section>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-erp-xs uppercase tracking-wide text-muted-foreground mb-1">{title}</h4>
      <div className="rounded-md border border-border divide-y divide-border">{children}</div>
    </div>
  )
}

function FieldRow({
  fieldKey, field, onChange, trailing,
}: {
  fieldKey: string
  field: FieldState
  onChange: (key: string, value: string) => void
  trailing?: React.ReactNode
}) {
  const proposed = field.proposed
  const changed = proposed != null && proposed !== '' && field.entered.trim() !== proposed
  const weak = field.confidence != null && field.confidence < LOW_CONFIDENCE

  return (
    <div className="px-2.5 py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
      <div className="min-w-0 flex-1">
        <div className="text-erp-sm truncate">{field.label}</div>
        <div className="flex items-center gap-2 mt-0.5">
          {proposed == null || proposed === '' ? (
            <span className="text-erp-xs text-muted-foreground">
              the machine read nothing here
            </span>
          ) : (
            <span className={cn('text-erp-xs',
              weak && 'rounded-sm bg-amber-100 px-1 text-amber-900 '
                + 'dark:bg-amber-500/20 dark:text-amber-200')}>
              machine read <strong className="font-mono">{proposed}</strong>
              {field.confidence != null && (
                <span className="ml-1 opacity-70">
                  {Math.round(field.confidence * 100)}%
                </span>
              )}
            </span>
          )}
          {trailing}
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        {/*
           THE POINT: when the two differ the row says so, and the machine's value is still
           on screen above. Nothing here overwrites `proposed`.
        */}
        {changed && (
          <span title="This will be recorded as a disagreement"
            className="inline-flex items-center gap-1 text-erp-xs text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-3 w-3" /> changed
          </span>
        )}
        <input
          inputMode="numeric"
          value={field.entered}
          onChange={(e) => onChange(fieldKey, e.target.value.replace(/[^\d]/g, ''))}
          className={cn('erp-input h-8 w-24 text-erp-sm text-right tabular-nums',
            changed && 'border-amber-400')}
          aria-label={field.label} />
        {changed && (
          <button type="button" title="Put the machine's reading back"
            onClick={() => onChange(fieldKey, proposed ?? '')}
            className="h-7 w-7 inline-flex items-center justify-center rounded-md
                       text-muted-foreground hover:text-foreground hover:bg-muted">
            <Undo2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  )
}
